export const SCENE_ID = 'minihra-hledani-kvetin'
export const BUBBLE_DISPLAY_MS = 5000
export const WRONG_BUBBLE_COUNT = 3

export type FlowerKind = 'hlavacek' | 'jahodnik' | 'sasanka' | 'pampeliska' | 'sedmikraska'

export interface FlowerItem {
  id: string
  kind: FlowerKind
  position: { x: number; y: number }
  width: number
  rotationDeg: number
  bubbleWidth?: number
  bubblePosition?: 'top' | 'bottom'
}

export interface FlowerTarget {
  kind: FlowerKind
  total: number
}

/** Correct flowers the player must find. Drives the panel counters. */
export const TARGETS: FlowerTarget[] = [
  { kind: 'hlavacek', total: 3 },
  { kind: 'jahodnik', total: 3 },
  { kind: 'sasanka', total: 3 },
]

const BG_WIDTH_PX = 1700
const BG_HEIGHT_PX = 3700
const HORIZONTAL_EDGE_MARGIN_PERCENT = 3
const TOP_EDGE_MARGIN_PERCENT = 6
const BOTTOM_EDGE_MARGIN_PERCENT = 3
const SCENE_WIDTH_TO_HEIGHT = BG_WIDTH_PX / BG_HEIGHT_PX
const MIN_FLOWER_WIDTH_PERCENT = 10.5

const FLOWER_ASPECT_RATIO: Record<FlowerKind, number> = {
  hlavacek: 336 / 338,
  jahodnik: 340 / 342,
  pampeliska: 353 / 364,
  sasanka: 324 / 342,
  sedmikraska: 358 / 331,
}

const CORRECT_FLOWER_KINDS: FlowerKind[] = ['hlavacek', 'jahodnik', 'sasanka']
const WRONG_FLOWER_KINDS: FlowerKind[] = ['pampeliska', 'sedmikraska']
const TARGET_WRONG_FLOWER_COUNT = 65
const MAX_CONSECUTIVE_WRONG_FAILURES = 200
const OVERLAP_GAP_PERCENT = 1.1

interface PlacementRect {
  x: number
  y: number
  width: number
  height: number
}

function createSeededRandom(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 0x100000000
  }
}

function getFlowerHeightPercent(kind: FlowerKind, widthPercent: number): number {
  return widthPercent * FLOWER_ASPECT_RATIO[kind] * SCENE_WIDTH_TO_HEIGHT
}

function isOverlapping(rect: PlacementRect, placed: PlacementRect[]): boolean {
  return placed.some((other) => {
    const separated =
      rect.x + rect.width + OVERLAP_GAP_PERCENT <= other.x ||
      other.x + other.width + OVERLAP_GAP_PERCENT <= rect.x ||
      rect.y + rect.height + OVERLAP_GAP_PERCENT <= other.y ||
      other.y + other.height + OVERLAP_GAP_PERCENT <= rect.y
    return !separated
  })
}

function tryPlaceFlower(
  kind: FlowerKind,
  widthPercent: number,
  random: () => number,
  placed: PlacementRect[],
  maxAttempts: number,
): { x: number; y: number } | null {
  const heightPercent = getFlowerHeightPercent(kind, widthPercent)
  const minX = HORIZONTAL_EDGE_MARGIN_PERCENT
  const minY = TOP_EDGE_MARGIN_PERCENT
  const maxX = 100 - HORIZONTAL_EDGE_MARGIN_PERCENT - widthPercent
  const maxY = 100 - BOTTOM_EDGE_MARGIN_PERCENT - heightPercent
  if (maxX <= minX || maxY <= minY) return null

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const x = minX + random() * (maxX - minX)
    const y = minY + random() * (maxY - minY)
    const rect: PlacementRect = { x, y, width: widthPercent, height: heightPercent }
    if (!isOverlapping(rect, placed)) {
      placed.push(rect)
      return { x, y }
    }
  }

  return null
}

function buildFlowers(): FlowerItem[] {
  const random = createSeededRandom(20260312)
  const placed: PlacementRect[] = []
  const flowers: FlowerItem[] = []
  const counters: Record<FlowerKind, number> = {
    hlavacek: 0,
    jahodnik: 0,
    sasanka: 0,
    pampeliska: 0,
    sedmikraska: 0,
  }

  const addFlower = (kind: FlowerKind, width: number, bubblePosition?: 'top' | 'bottom'): boolean => {
    const effectiveWidth = Math.max(width, MIN_FLOWER_WIDTH_PERCENT)
    const pos = tryPlaceFlower(kind, effectiveWidth, random, placed, 5000)
    if (!pos) return false
    counters[kind]++
    flowers.push({
      id: `${kind}-${counters[kind]}`,
      kind,
      position: { x: pos.x, y: pos.y },
      width: effectiveWidth,
      rotationDeg: random() * 360,
      bubbleWidth: CORRECT_FLOWER_KINDS.includes(kind) ? 30 : undefined,
      bubblePosition,
    })
    return true
  }

  // Place the 3x3 required flowers first so game logic and counters stay unchanged.
  CORRECT_FLOWER_KINDS.forEach((kind) => {
    for (let i = 0; i < 3; i++) {
      const startWidth = kind === 'hlavacek' ? 10.5 : 9.5
      let placedCorrect = false
      for (let width = startWidth; width >= 7.5; width -= 0.5) {
        const bubblePosition: 'top' | 'bottom' = i % 2 === 0 ? 'top' : 'bottom'
        if (addFlower(kind, width, bubblePosition)) {
          placedCorrect = true
          break
        }
      }
      if (!placedCorrect) {
        throw new Error(`Could not place required flower kind: ${kind}`)
      }
    }
  })

  // Fill the rest of the garden with as many wrong flowers as we can place.
  let consecutiveFailures = 0
  let safetyCounter = 0
  while (
    counters.pampeliska + counters.sedmikraska < TARGET_WRONG_FLOWER_COUNT &&
    consecutiveFailures < MAX_CONSECUTIVE_WRONG_FAILURES
  ) {
    safetyCounter++
    if (safetyCounter > 50000) break

    const kind = WRONG_FLOWER_KINDS[Math.floor(random() * WRONG_FLOWER_KINDS.length)]
    const width = kind === 'pampeliska' ? 6.9 + random() * 1.6 : 6.7 + random() * 1.5
    const wasPlaced = addFlower(kind, width)

    if (wasPlaced) {
      consecutiveFailures = 0
    } else {
      consecutiveFailures++
    }
  }

  return flowers
}

/** All flowers on the scene. Positions in % of the items container. */
export const FLOWERS: FlowerItem[] = buildFlowers()

export const CORRECT_KINDS = new Set<FlowerKind>(TARGETS.map((t) => t.kind))
